<template>
  <div class="query-page" :class="scheme ? `page-theme-${scheme.theme || 'blue'}` : ''">
    <div class="query-shell">
      <div v-if="pageLoading" class="panel state-panel">
        <el-icon class="spinning" :size="38"><Loading /></el-icon>
        <p>正在加载查分信息...</p>
      </div>

      <div v-else-if="pageError" class="panel state-panel">
        <el-icon class="error-icon" :size="54"><CircleClose /></el-icon>
        <h2>{{ pageError }}</h2>
        <p>请检查链接是否有效，或联系发布者确认查分项目状态。</p>
      </div>

      <div v-else-if="scheme" class="panel query-card" :class="[`theme-${scheme.theme || 'blue'}`, { 'has-result': queryResult }]">
        <img v-if="scheme.query_image_url" :src="resolveAssetUrl(scheme.query_image_url)" class="query-image" alt="" />
        <div class="query-header">
          <div class="title-icon">
            <el-icon><Trophy /></el-icon>
          </div>
          <div>
            <div class="query-eyebrow">成绩查询</div>
            <h1>{{ scheme.title }}</h1>
            <p>{{ scheme.query_prompt || scheme.description || '请如实填写以下信息，系统将为您查找对应的成绩记录。' }}</p>
          </div>
        </div>

        <div v-if="!queryResult" class="query-form">
          <div class="query-progress">
            <span>{{ scheme.query_conditions.length }} 项信息核验</span>
            <strong>精准匹配个人成绩</strong>
          </div>
          <div v-for="cond in scheme.query_conditions" :key="cond" class="field-group">
            <label>{{ cond }}</label>
            <input
              v-model="queryData[cond]"
              :placeholder="`请输入${cond}`"
              autocomplete="off"
              autocapitalize="off"
              enterkeyhint="search"
              @keyup.enter="doSearch"
            />
          </div>

          <div class="form-actions">
            <button class="primary-btn" @click="doSearch" :disabled="searching">
              <span>
                <el-icon v-if="searching" class="spinning"><Loading /></el-icon>
                <el-icon v-else><Search /></el-icon>
                {{ searching ? '查询中...' : '查询成绩' }}
              </span>
            </button>
            <button class="secondary-btn" @click="clearForm">
              <span><el-icon><RefreshLeft /></el-icon>清空</span>
            </button>
          </div>

          <div v-if="searchError" class="search-error">
            <el-icon><Warning /></el-icon>{{ searchError }}
          </div>
        </div>

        <div v-else class="result-section">
          <div class="result-header">
            <el-icon><CircleCheck /></el-icon>
            <div>
              <h2>查询成功</h2>
              <p>共找到 {{ queryResult.results.length }} 条记录</p>
            </div>
          </div>

          <div class="result-summary">
            <div v-for="cond in scheme.query_conditions" :key="cond">
              <span>{{ cond }}</span>
              <strong>{{ queryData[cond] }}</strong>
            </div>
          </div>

          <div v-for="(record, idx) in queryResult.results" :key="idx" class="result-record">
            <div v-if="queryResult.results.length > 1" class="record-title">记录 {{ idx + 1 }}</div>
            <div v-for="group in resultGroups" :key="group.title" class="result-group">
              <div class="result-group-title">{{ group.title }}</div>
              <div v-for="header in group.fields" :key="header" class="result-row" :class="getRowClass(header, record[header])">
                <span class="row-label">{{ header }}</span>
                <span class="row-value">{{ record[header] || '-' }}</span>
                <span v-if="getAnalysisText(header)" class="row-analysis">{{ getAnalysisText(header) }}</span>
              </div>
            </div>
          </div>

          <button class="secondary-btn back-btn" @click="clearResult">
            <el-icon><ArrowLeft /></el-icon>
            重新查询
          </button>
        </div>
      </div>

      <div class="powered-by">Powered by <strong>爱教务智能教务系统</strong></div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, reactive, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import {
  Loading, CircleClose, Trophy, Search, RefreshLeft, Warning, CircleCheck, ArrowLeft
} from '@element-plus/icons-vue'
import { scoreQueryApi } from '@/api'

const route = useRoute()
const code = route.params.code

const pageLoading = ref(true)
const pageError = ref('')
const scheme = ref(null)
const queryData = reactive({})
const searching = ref(false)
const searchError = ref('')
const queryResult = ref(null)
const resultGroups = computed(() => {
  if (!queryResult.value) return []
  if (queryResult.value.field_groups?.length) return queryResult.value.field_groups
  return [{ title: '查询结果', fields: queryResult.value.headers || [] }]
})

function getApiOrigin() {
  const baseUrl = process.env.VUE_APP_API_BASE_URL || '/api'
  if (/^https?:\/\//i.test(baseUrl)) {
    return new URL(baseUrl).origin
  }
  return window.location.origin
}

function resolveAssetUrl(url) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  return `${getApiOrigin()}${url}`
}

onMounted(async () => {
  try {
    const data = await scoreQueryApi.getPublicScheme(code)
    scheme.value = data
    data.query_conditions.forEach(cond => {
      queryData[cond] = ''
    })
  } catch (e) {
    pageError.value = e.message || '该查分项目不存在或已被关闭'
  } finally {
    pageLoading.value = false
  }
})

async function doSearch() {
  searchError.value = ''
  for (const cond of scheme.value.query_conditions) {
    if (!queryData[cond] || queryData[cond].trim() === '') {
      searchError.value = `请填写【${cond}】`
      return
    }
  }
  searching.value = true
  try {
    const reqData = {}
    scheme.value.query_conditions.forEach(cond => {
      reqData[cond] = queryData[cond]
    })
    queryResult.value = await scoreQueryApi.searchScore(code, reqData)
  } catch (e) {
    searchError.value = e.message || '未找到匹配的成绩，请核实输入信息'
  } finally {
    searching.value = false
  }
}

function clearForm() {
  scheme.value.query_conditions.forEach(cond => {
    queryData[cond] = ''
  })
  searchError.value = ''
}

function clearResult() {
  queryResult.value = null
  searchError.value = ''
  clearForm()
}

function formatNumber(value) {
  if (value === undefined || value === null) return ''
  return Number.isInteger(value) ? String(value) : String(value)
}

function getAnalysisText(header) {
  const stats = queryResult.value?.analysis?.[header]
  if (!stats) return ''
  const parts = []
  if (stats.max !== undefined) parts.push(`最高 ${formatNumber(stats.max)}`)
  if (stats.min !== undefined) parts.push(`最低 ${formatNumber(stats.min)}`)
  if (stats.avg !== undefined) parts.push(`均分 ${formatNumber(stats.avg)}`)
  return parts.join(' · ')
}

function getRowClass(header, value) {
  const classes = []
  if (scheme.value?.query_conditions?.includes(header)) classes.push('result-row-key')
  const num = parseFloat(value)
  if (!Number.isNaN(num) && String(value).trim() !== '') classes.push('result-row-score')
  return classes
}
</script>

<style scoped>
.query-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 14px;
  background:
    linear-gradient(180deg, rgba(37, 99, 235, 0.08), rgba(20, 184, 166, 0.04) 42%, rgba(255, 255, 255, 0)),
    #f6f8fb;
  font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}
.page-theme-blue {
  background:
    linear-gradient(180deg, rgba(37, 99, 235, 0.14), rgba(14, 165, 233, 0.06) 44%, rgba(255, 255, 255, 0)),
    #f6f8fb;
}
.page-theme-green {
  background:
    linear-gradient(180deg, rgba(22, 163, 74, 0.14), rgba(20, 184, 166, 0.06) 44%, rgba(255, 255, 255, 0)),
    #f5fbf7;
}
.page-theme-gold {
  background:
    linear-gradient(180deg, rgba(217, 119, 6, 0.16), rgba(250, 204, 21, 0.08) 44%, rgba(255, 255, 255, 0)),
    #fffaf0;
}
.page-theme-dark {
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(31, 41, 55, 0.94) 48%, rgba(17, 24, 39, 0.98)),
    #111827;
}
.query-shell {
  width: 100%;
  max-width: 560px;
}
.panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.09);
  overflow: hidden;
}
.state-panel {
  padding: 56px 32px;
  text-align: center;
  color: #64748b;
}
.state-panel h2 { margin: 16px 0 8px; color: #1e293b; font-size: 20px; }
.state-panel p { margin: 0; }
.error-icon { color: #ef4444; }
.query-image {
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  display: block;
}
.query-header {
  display: flex;
  gap: 14px;
  padding: 24px 24px 20px;
  border-bottom: 1px solid #edf2f7;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}
.title-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #ecfeff;
  color: #0891b2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.theme-blue {
  --theme-color: #2563eb;
  --theme-soft: #eff6ff;
  --theme-border: #dbeafe;
  --theme-text: #1d4ed8;
  --theme-header: linear-gradient(135deg, #eff6ff 0%, #ffffff 64%);
  --theme-card-border: #bfdbfe;
}
.theme-green {
  --theme-color: #16a34a;
  --theme-soft: #f0fdf4;
  --theme-border: #bbf7d0;
  --theme-text: #15803d;
  --theme-header: linear-gradient(135deg, #ecfdf5 0%, #ffffff 64%);
  --theme-card-border: #bbf7d0;
}
.theme-gold {
  --theme-color: #d97706;
  --theme-soft: #fffbeb;
  --theme-border: #fde68a;
  --theme-text: #b45309;
  --theme-header: linear-gradient(135deg, #fffbeb 0%, #ffffff 62%);
  --theme-card-border: #fcd34d;
}
.theme-dark {
  --theme-color: #0f172a;
  --theme-soft: #1f2937;
  --theme-border: #374151;
  --theme-text: #e5e7eb;
  --theme-header: linear-gradient(135deg, #111827 0%, #1f2937 100%);
  --theme-card-border: #475569;
}
.query-card {
  border-color: var(--theme-card-border, #e2e8f0);
}
.query-card .query-header {
  background: var(--theme-header);
  border-color: var(--theme-border);
}
.query-card .title-icon {
  background: var(--theme-soft);
  color: var(--theme-color);
}
.query-card .query-eyebrow,
.query-card .query-progress strong,
.query-card .result-summary strong,
.query-card .result-row-score .row-value {
  color: var(--theme-text);
}
.query-card .query-progress,
.query-card .result-summary div {
  background: var(--theme-soft);
  border-color: var(--theme-border);
}
.query-card .primary-btn {
  background: var(--theme-color);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.16);
}
.theme-gold .query-header h1,
.theme-gold .result-header h2 {
  color: #92400e;
}
.theme-green .query-header h1,
.theme-green .result-header h2 {
  color: #14532d;
}
.theme-dark .query-header {
  background: linear-gradient(180deg, #111827, #1f2937);
  border-color: #374151;
}
.theme-dark.query-card {
  background: #111827;
  border-color: #475569;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.28);
}
.theme-dark .query-header h1 {
  color: #fff;
}
.theme-dark .query-header p {
  color: #cbd5e1;
}
.theme-dark .query-form,
.theme-dark .result-section {
  background: #111827;
}
.theme-dark .field-group label,
.theme-dark .result-group-title,
.theme-dark .row-label,
.theme-dark .result-header h2 {
  color: #e5e7eb;
}
.theme-dark .field-group input,
.theme-dark .secondary-btn,
.theme-dark .result-record {
  background: #1f2937;
  border-color: #374151;
  color: #f8fafc;
}
.theme-dark .record-title,
.theme-dark .result-group-title,
.theme-dark .result-row-key {
  background: #172033;
}
.theme-dark .result-row,
.theme-dark .result-group {
  border-color: #374151;
}
.theme-dark .row-value {
  color: #fff;
}
.theme-dark .result-header p,
.theme-dark .powered-by,
.theme-dark .powered-by strong {
  color: #cbd5e1;
}
.query-eyebrow {
  margin-bottom: 5px;
  color: #0891b2;
  font-size: 12px;
  font-weight: 800;
}
.query-header h1 {
  margin: 0 0 6px;
  color: #0f172a;
  font-size: 22px;
  line-height: 1.3;
}
.query-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.55;
}
.query-form, .result-section {
  padding: 24px;
}
.query-progress {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding: 11px 13px;
  border-radius: 10px;
  background: #f0fdfa;
  border: 1px solid #ccfbf1;
  color: #64748b;
  font-size: 12px;
}
.query-progress strong {
  color: #0f766e;
  font-weight: 750;
}
.field-group {
  display: grid;
  gap: 7px;
  margin-bottom: 15px;
}
.field-group label {
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}
.field-group input {
  height: 50px;
  padding: 0 15px;
  border: 1px solid #dbe3ef;
  border-radius: 10px;
  color: #0f172a;
  font-size: 15px;
  outline: none;
  -webkit-appearance: none;
}
.field-group input:focus {
  border-color: #0891b2;
  box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}
.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  align-items: stretch;
}
.primary-btn, .secondary-btn {
  box-sizing: border-box;
  min-height: 50px;
  height: 50px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 750;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  cursor: pointer;
  line-height: 1;
  white-space: nowrap;
}
.primary-btn span, .secondary-btn span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 20px;
}
.primary-btn .el-icon, .secondary-btn .el-icon {
  flex: 0 0 auto;
}
.primary-btn {
  flex: 1;
  border: 0;
  background: #0891b2;
  color: #fff;
  box-shadow: 0 10px 24px rgba(8, 145, 178, 0.2);
}
.primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.secondary-btn {
  padding: 0 18px;
  border: 1px solid #dbe3ef;
  background: #fff;
  color: #475569;
}
.search-error {
  margin-top: 14px;
  padding: 11px 13px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
  display: flex;
  gap: 7px;
  align-items: center;
}
.result-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  color: #16a34a;
}
.result-header h2 { margin: 0; color: #0f172a; font-size: 18px; }
.result-header p { margin: 2px 0 0; color: #64748b; font-size: 13px; }
.result-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 8px;
  margin-bottom: 14px;
}
.result-summary div {
  padding: 9px 10px;
  border-radius: 8px;
  background: #f0fdfa;
  border: 1px solid #ccfbf1;
}
.result-summary span {
  display: block;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
}
.result-summary strong {
  display: block;
  margin-top: 3px;
  color: #0f766e;
  font-size: 14px;
  word-break: break-word;
}
.result-record {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 14px;
}
.record-title {
  padding: 9px 13px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}
.result-row {
  display: grid;
  grid-template-columns: minmax(88px, 0.8fr) minmax(0, 1fr);
  gap: 10px;
  padding: 12px 14px;
  border-top: 1px solid #edf2f7;
}
.result-row:first-child { border-top: 0; }
.result-group {
  border-top: 1px solid #edf2f7;
}
.result-group:first-child {
  border-top: 0;
}
.result-group-title {
  padding: 10px 14px;
  background: #f8fafc;
  color: #334155;
  font-size: 13px;
  font-weight: 800;
}
.result-row-key {
  background: #f8fafc;
}
.result-row-score .row-value {
  font-size: 17px;
  color: #0e7490;
}
.row-label {
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}
.row-value {
  min-width: 0;
  color: #0f172a;
  font-size: 15px;
  font-weight: 750;
  text-align: right;
  word-break: break-word;
}
.row-analysis {
  grid-column: 1 / -1;
  color: #047857;
  background: #ecfdf5;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
}
.back-btn { margin-top: 4px; }
.powered-by {
  margin-top: 14px;
  text-align: center;
  color: #94a3b8;
  font-size: 12px;
}
.powered-by strong { color: #64748b; }
.spinning { animation: spin 1s linear infinite; }
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@media (max-width: 480px) {
  .query-page {
    align-items: flex-start;
    padding: 10px 10px calc(16px + env(safe-area-inset-bottom));
  }
  .panel {
    border-radius: 14px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
  }
  .query-image {
    max-height: 150px;
  }
  .query-header {
    gap: 10px;
    padding: 16px 16px 14px;
  }
  .title-icon {
    width: 40px;
    height: 40px;
    border-radius: 9px;
  }
  .query-header h1 {
    font-size: 18px;
  }
  .query-header p {
    font-size: 12px;
  }
  .query-form, .result-section { padding: 16px; }
  .query-progress {
    display: grid;
    gap: 2px;
  }
  .field-group {
    margin-bottom: 13px;
  }
  .field-group input {
    height: 50px;
    font-size: 16px;
  }
  .form-actions { flex-direction: column; }
  .primary-btn, .secondary-btn {
    width: 100%;
    min-height: 52px;
    height: 52px;
    flex: 0 0 auto;
  }
  .primary-btn {
    font-size: 16px;
  }
  .secondary-btn {
    width: 100%;
    font-size: 15px;
  }
  .result-header {
    margin-bottom: 14px;
  }
  .result-summary {
    grid-template-columns: 1fr;
  }
  .result-row {
    grid-template-columns: 1fr;
    gap: 5px;
    padding: 11px 12px;
  }
  .row-value {
    text-align: left;
    font-size: 16px;
  }
  .row-analysis {
    margin-top: 2px;
  }
  .back-btn {
    position: sticky;
    bottom: calc(10px + env(safe-area-inset-bottom));
    width: 100%;
    background: #fff;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.12);
  }
  .powered-by {
    margin-top: 10px;
  }
}
</style>
